<template>
  <modal :name="'modal-calendar'" close-button>
    <div slot="content">
      <div class="row m0 center-xs">
        <h4 class="col-xs-12 secondary-font h5 align-center m0 letter-spacing-1 uppercase weight-700">
          {{ title }}
        </h4>
        <p class="col-xs-12 h6 align-center weight-400 mb0">
          {{ subtitle }} 
        </p>
        <router-link v-if="this.getActivePeriod !== 3 && $store.state.calendar.delivery === 'showroom'" :to="'/i/showroom'" target="_blank" class="col-xs-12 h6 align-center weight-400 underline pointer">
          {{ $t("See where our store is") }}
        </router-link>
        <datepicker-c v-if="!loading" :locale="storeView.i18n.defaultLanguage" :min-date="getFirstAvaliableDate" class="col-xs-12 center-xs inline-block h-full mb20" :attributes="attrs" @input="changeDateRange" @transition-end="changeStylesOnMonthChange" v-model="date" :disabled-dates="getFullBookedDates" :first-day-of-week="2" is-required />
        <div class="col-xs-12 legend">
          <div class="row middle-xs">
            <div class="color-block black col-xs-2">{{ startDateShort }}</div>
            <div class="col-xs align-left weight-400 nowrap">{{ startDateText }}</div>
            <div class="col-xs-4 weight-700 pr0 align-right">{{ startDateFull }}</div>
          </div>
          <div class="row middle-xs">
            <div class="color-block red col-xs-2">{{ endDateShort }}</div>
            <div class="col-xs align-left weight-400">{{ $t("Package return day") }}</div>
            <div div class="col-xs-4 weight-700 pr0 align-right">{{ endDateFull }}<p v-if="isHourInfoVisible" class="hour-paragraph">{{ $t("by") }} 12:00</p></div>
          </div>
          <div class="row middle-xs">
            <div class="color-block striped col-xs-2"></div>
            <div class="col-xs-6 align-left weight-400 nowrap">{{ $t("Date not available") }}</div>
          </div>
          <div class="row middle-xs mb15">
            <div class="color-block white col-xs-2"></div>
            <div class="col-xs-6 align-left weight-400 nowrap">{{ $t("Date available") }}</div>
          </div>
          <div class="row middle-xs">
            <button-full-rectangonal class="w-100" @click.native="chooseDateAndClose" :disabled="!getDateRangeString">{{ $t("Choose") }} <span v-if="getDateRangeString">{{ getDateRangeString }}</span></button-full-rectangonal>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex'
import Modal from 'theme/components/core/Modal.vue'
import ButtonFullRectangonal from 'theme/components/theme/ButtonFullRectangonal.vue'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'

export default {
  components: {
    Modal,
    ButtonFullRectangonal,
    DatepickerC: () => import('v-calendar/lib/components/date-picker.umd'),
  },
  watch: {
    getSelectedDate: {
      handler (value) {
        if (!value) this.date = null
      }
    }
  },
  data () {
    return {
      date: null,
      loading: true,
    }
  },
  computed: {
    ...mapGetters({
      getFirstAvaliableDate: 'calendar/getFirstAvaliableDate',
      getBookedDatesFull: 'calendar/getBookedDatesFull',
      getBlockedDays: 'calendar/getBlockedDays',
      getActivePeriod: 'calendar/getActivePeriod',
      getSelectedDate: 'calendar/getSelectedDate',
      getCurrentProduct: 'product/getCurrentProduct',
      getUserBalance: 'user/getBalance'
    }),
    attrs () {
      return [
        {
          highlight: {
            start: { color: 'blue', fillMode: 'solid' },
            base: { color: 'blue', fillMode: 'light' },
            end: { color: 'blue', fillMode: 'solid' }
          },
          dates: { start: this.getFirstAvaliableDate, span: 1 }
        }
      ]
    },
    getFullBookedDates () {
      return this.getBookedDatesFull.concat(this.getBlockedDays)
    },
    startDateText () {
      if (this.getActivePeriod === 3) return this.$t('Try-on date')
      return this.$t("Package delivery day")
    },
    startDateFull () {
      if (!this.date) return
      let day = this.date.getDate() < 10 ? `0${this.date.getDate()}` : this.date.getDate();
      let month = this.date.getMonth() + 1 < 10 ? `0${this.date.getMonth() + 1}` : this.date.getMonth() + 1;
      let year = this.date.getFullYear();
      return  `${day}.${month}.${year}`
    },
    startDateShort () {
      if (!this.startDateFull) return
      return this.startDateFull.substring(0, 5)
    },
    endDateFull () {
      if (!this.date) return;
      let endDate = new Date(this.date);
      endDate.setDate(endDate.getDate() + Number(this.activePeriod) - 1);

      let day = endDate.getDate() < 10 ? `0${endDate.getDate()}` : endDate.getDate();
      let month = endDate.getMonth() + 1 < 10 ? `0${endDate.getMonth() + 1}` : endDate.getMonth() + 1;
      let year = endDate.getFullYear();

      return `${day}.${month}.${year}`;
    },
    endDateShort () {
      if (!this.endDateFull) return
      return this.endDateFull.substring(0, 5)
    },
    getDateRangeString () {
      if (!(this.startDateFull && this.endDateFull)) return
      let isSameMonth = this.startDateFull.substring(3, 5) === this.endDateFull.substring(3, 5)
      if (isSameMonth) return this.startDateFull.substring(0, 2) + '-' + this.endDateFull
      return this.startDateShort + '-' + this.endDateFull
    },
    isHourInfoVisible () {
      return this.$store.state.calendar.delivery === 'omnipack' && this.endDateFull
    },
    title() {
      if (this.getActivePeriod === 3) return this.$t('select the try-on date')
      return this.$t('select the rental date')
    },
    subtitle () {
      if (this.getActivePeriod === 3 && this.$store.state.calendar.delivery === 'showroom') return this.$t("In-store fittings are free and held daily. You do not need to make an appointment")
      if (this.getActivePeriod === 3 && this.$store.state.calendar.delivery === 'omnipack') return this.$t("Try-on with shipping are only available on Wednesdays")
      if (this.$store.state.calendar.delivery === 'omnipack') return this.$t("When you want the package delivered to you")
      if (this.$store.state.calendar.delivery === 'showroom') return this.$t("When you want to pick up the creation in-store")
      return this.$t('Pick a delivery date 1-2 days before your event')
    },
    activePeriod () {
      // for try-on we fake 2 days on calendar but we send 3 days to backend
      return this.getActivePeriod === 3 ? 2 : this.getActivePeriod
    },
    storeView () {
      return currentStoreView()
    } 
  },
  mounted () {
    this.$nextTick(() => {
      this.loading = false
    })
  },
  updated () {
    if (this.date) this.changeDaysStyle(this.date)
  },
  methods: {
    changeDateRange (value) {
      if (value) this.changeDaysStyle(value)
      this.attrs[0].dates.start = this.date
      this.attrs[0].dates.span = Number(this.activePeriod)
      if (this.getCurrentProduct.subscription_points && this.getUserBalance >= 1) {
        this.getCurrentProduct.product_option.extension_attributes.subscription = true
      }
    },
    changeDaysStyle (firstDayValue) {
      if (firstDayValue) this.resetDaysStyle()
      const days = []
      for (let i = 0; i < this.activePeriod; i++) {
        const nextDay = new Date(firstDayValue)
        nextDay.setDate(nextDay.getDate() + i)
        days.push(nextDay)
      }
      const idOfDays = days.map(day => "id-" + day.toISOString().substring(0, 10))
      idOfDays.forEach((day, index) => { 
        const element = document.getElementsByClassName(day)[0]
        if (!element) return
        if (index === 0) element.querySelector(".vc-day-content").classList.add('first-day-content')
        else if (index === days.length - 1) element.querySelector(".vc-day-content").classList.add('last-day-content')
        else element.querySelector(".vc-day-content").classList.add('between-day-content')
      })
    },
    resetDaysStyle() {
      const firstDay = document.querySelector('.first-day-content')
      const betweenDays = document.querySelectorAll('.between-day-content')
      const lastDay = document.querySelector('.last-day-content')
      
      if (firstDay) firstDay.classList.remove('first-day-content')
      if (betweenDays) 
        betweenDays.forEach(element => {
          element.classList.remove('between-day-content');
        });
      if (lastDay) lastDay.classList.remove('last-day-content')
    },
    getEndDate (activePeriod = this.getActivePeriod) {
      let p = activePeriod ? Number(activePeriod) - 1 : null
      let d = this.date ? new Date(this.date) : null
      return (typeof p === 'number' && d) ? d.setUTCDate(d.getUTCDate() + p) : null
    },
    chooseDateAndClose() {
      if (!this.date) return
      this.$store.commit('calendar/setDate', this.date)
      this.$store.commit('calendar/setDateValidation', !!this.date)
      this.selectedDate = new Date(this.date).toLocaleDateString('pl-PL') + ' - ' + new Date(this.getEndDate(this.activePeriod)).toLocaleDateString('pl-PL')
      this.$store.commit('calendar/setSelectedDate', this.selectedDate)
      this.$bus.$emit('setCalendarDate', { start: this.date, end: this.getEndDate() })
      this.$bus.$emit('modal-hide', 'modal-calendar')
    },
    changeStylesOnMonthChange() {
      this.$nextTick(() => {
        this.changeDaysStyle(this.date)
      })
    }
  }
}
</script>

<style lang="scss">
.modal-content {
  @media (max-height: 767px) {
    padding: 20px !important;
  }
  padding: 35px !important;
}
.vc-pane-container {
  width: unset !important;
}
.vc-container {
  width: 100% !important;
  border: 0 !important;
}
.vc-header {
  padding-bottom: 10px !important;
}
.vc-title {
  @media (max-width: 767px) {
    font-size: 12px !important;
  }
  font-size: 17px !important;
  font-family: 'Poppins';
  font-weight: 700 !important;
}
.vc-arrows-container {
  padding: 8px 0 !important;
}
.vc-arrow .vc-svg-icon {
  color: #2D2D2D;
}
.vc-weekday {
  color: #C2C2C2 !important;
  font-size: 12px !important;
  font-family: 'Poppins';
  font-weight: 500 !important;
}
.vc-day {
  @media (max-width: 767px) {
    width: 30px !important;
    height: 30px !important;
  }
  height: 43px !important;
  min-width: 30px;
  min-height: 30px !important;
  font-family: 'Poppins';
  font-weight: 500;
  font-size: 12px;
  margin: 1px;
  align-items: stretch !important;
  &.is-not-in-month {
    border: 1px solid #DFDFDF;
    border-style: dashed;
  }
}
.vc-day.is-not-in-month * {
  opacity: 1 !important;
}
.vc-day-content {
  background-color: #fff;
  color: #9D9D9D !important;
  width: 100% !important;
  height: auto !important;
  border-radius: 0 !important;
  &:not(.is-disabled):hover {
    background-color: #312F2F !important;
    border: 1px solid #312F2F !important;
    color: #fff !important;
    font-weight: 500 !important;
  }
  &:focus {
    font-weight: 500 !important;
  }
}
.vc-day:not(.is-not-in-month) .vc-day-content {
  border: 1px solid #DFDFDF;
}
.vc-day:not(.is-not-in-month) .vc-day-content.is-disabled {
  color: #A7A7A7;
  border: none;
  cursor: default;
  background: repeating-linear-gradient(
    -45deg,
    #fff,
    #fff 3px,
    #c0c0c0 3px,
    #c0c0c0 6px
  );
  &:hover {
    border: none !important;
  }
}
.vc-highlights {
  z-index: 0 !important;
}
.mobile-padding {
  @media (min-width: 767px) {
      padding-top: 0px;
    }
}
.vc-highlight {
  background-color: initial !important;
}
.vc-weeks {
  @media (max-width: 767px) {
    grid-template-columns: repeat(7, 30px) !important;
    gap: 6px !important;
  }
  grid-template-columns: repeat(7, 43px) !important;
  gap: 8.75px !important;
  padding: 0 !important;
}
.vc-popover-content.direction-bottom {
  background-color: #3E3E3E !important;
}
.vc-nav-item.is-active {
  background-color: #fff !important;
  color: #3E3E3E !important;
}
.vc-highlights + span {
  background: #312F2F !important;
  border: 1px solid #312F2F !important;
  color: #fff !important;
  font-weight: 500 !important;
  &.first-day-content {
    background: #312F2F !important;
    border: 1px solid #312F2F !important;
    color: #fff !important;
  }
  &.between-day-content {
    background: #F5D2D2 !important;
    border: 1px solid #312F2F !important;
    color: #312F2F !important;
  }
  &.last-day-content {
    background: #FF7878 !important;
    border: 1px solid #312F2F;
    color: #fff !important;
  }
} 
</style>

<style lang="scss" scoped>
/deep/ .modal-container {
  width: 450px !important;
}
.nowrap {
  white-space: nowrap;
}
.color-block {
  --block-height: 30px;
  @media (max-width: 767px) {
    max-width: 30px;
    padding: 0;
    font-size: 10px;
  }
  border: 1px solid #DFDFDF;
  color: #fff;
  height: var(--block-height);
  line-height: var(--block-height);
  width: 80px;
  &.black {
    background-color: #312F2F;
  }
  &.red {
    background-color: #FF7878;
  }
  &.striped {
    color: #DFDFDF;
    background: repeating-linear-gradient(
      -45deg,
     #fff,
     #fff 3px,
     #c0c0c0 3px,
     #c0c0c0 6px
    );
  }
  &.white {
    background-color: #fff;
  }
}
.hour-paragraph {
  margin: 0;
  white-space: nowrap;
  float: right;
}
.legend {
  font-size: 14px;
  @media (max-width: 767px) {
    font-size: 12px;
  }
  & > div {
    height: 42px;
  }
}
</style>
